import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  getImageGeneratorErrorMessage,
  ImageGeneratorHistoryRecord,
  preferGenerativeCdnUrl,
  saveImageGeneratorToLibrary,
} from "../../app/api/imageGenerator";
import { imageGeneratorSessionTitle } from "./imageGeneratorSession";
import ImageGeneratorLibraryModal from "./ImageGeneratorLibraryModal";
import {
  IMAGE_GENERATOR_LIBRARY_EMPTY_HINT,
  IMAGE_GENERATOR_LIBRARY_HEADLINE,
  IMAGE_GENERATOR_LIBRARY_LOAD_ERROR,
  IMAGE_GENERATOR_LIBRARY_LOADING,
  IMAGE_GENERATOR_LIBRARY_NO_IMAGE,
  IMAGE_GENERATOR_LIBRARY_RENAME_ERROR,
  IMAGE_GENERATOR_LIBRARY_RENAME_SUCCESS,
  IMAGE_GENERATOR_LIBRARY_TRY_AGAIN,
} from "./generativePlatformLibraryConstants";
import {
  imageGeneratorLibraryQueryKey,
  IMAGE_GENERATOR_LIBRARY_LIST_LIMIT,
} from "./generativePlatformQueryKeys";
import { useImageGeneratorLibraryQuery } from "./useImageGeneratorLibraryQuery";
import useImageGeneratorPreviews from "./useImageGeneratorPreviews";

type Props = {
  onOpen: (id: number) => void;
};

const absolutePreviewSrc = (url: string) => {
  const value = preferGenerativeCdnUrl(url);
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }
  return "";
};

const ImageGeneratorTracker: React.FC<Props> = ({ onOpen }) => {
  const queryClient = useQueryClient();
  const {
    data: items = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useImageGeneratorLibraryQuery(IMAGE_GENERATOR_LIBRARY_LIST_LIMIT, 0);
  const [renameItem, setRenameItem] =
    useState<ImageGeneratorHistoryRecord | null>(null);
  const [renaming, setRenaming] = useState(false);

  const previewItems = useMemo(
    () =>
      items.map(item => ({
        id: String(item.id),
        imageUrl: item.imageUrl,
      })),
    [items]
  );
  const previews = useImageGeneratorPreviews(previewItems);

  const handleRename = async (payload: { fileName: string; name: string }) => {
    if (!renameItem || renaming) {
      return;
    }
    setRenaming(true);
    try {
      const updated = await saveImageGeneratorToLibrary(renameItem.id, payload);
      queryClient.setQueryData<ImageGeneratorHistoryRecord[]>(
        imageGeneratorLibraryQueryKey(IMAGE_GENERATOR_LIBRARY_LIST_LIMIT, 0),
        current =>
          (current || []).map(item => (item.id === updated.id ? updated : item))
      );
      setRenameItem(null);
      toast.success(IMAGE_GENERATOR_LIBRARY_RENAME_SUCCESS);
    } catch (nextError) {
      toast.error(
        getImageGeneratorErrorMessage(
          nextError,
          IMAGE_GENERATOR_LIBRARY_RENAME_ERROR
        )
      );
    } finally {
      setRenaming(false);
    }
  };

  if (isLoading && items.length === 0) {
    return (
      <p className="py-10 text-sm text-tertiary">
        {IMAGE_GENERATOR_LIBRARY_LOADING}
      </p>
    );
  }

  if (isError) {
    return (
      <div className="py-10">
        <p className="text-sm text-secondary">
          {getImageGeneratorErrorMessage(
            error,
            IMAGE_GENERATOR_LIBRARY_LOAD_ERROR
          )}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-3 text-sm font-medium text-secondary underline"
        >
          {IMAGE_GENERATOR_LIBRARY_TRY_AGAIN}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-light bg-white p-8 text-center">
        <p className="font-medium text-secondary">
          {IMAGE_GENERATOR_LIBRARY_HEADLINE}
        </p>
        <p className="mt-2 text-sm text-tertiary">
          {IMAGE_GENERATOR_LIBRARY_EMPTY_HINT}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-secondary">
        {IMAGE_GENERATOR_LIBRARY_HEADLINE}
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map(item => {
          const library = item.review?.library;
          const hasSheet = item.details?.designSheet?.status === "generated";
          const title =
            library?.name ||
            library?.fileName ||
            imageGeneratorSessionTitle(item);
          const previewUrl =
            previews[String(item.id)] ||
            absolutePreviewSrc(item.imageUrl || "");
          const kind =
            item.outputType === "style" || item.details?.outputType === "Style"
              ? "Style"
              : "Pattern";
          const savedAt =
            item.details?.designSheet?.finishedAt ||
            library?.savedAt ||
            item.createdAt;
          const savedLabel = (() => {
            const date = new Date(savedAt);
            if (Number.isNaN(date.getTime())) {
              return "";
            }
            return date.toLocaleString([], {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            });
          })();
          return (
            <div
              key={item.id}
              className="overflow-hidden rounded-2xl border border-light bg-white text-left shadow-sm transition-shadow hover:shadow-md"
            >
              <button
                type="button"
                onClick={() => onOpen(item.id)}
                className="block w-full text-left"
              >
                <div className="aspect-[3/4] bg-gray">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-tertiary">
                      {IMAGE_GENERATOR_LIBRARY_NO_IMAGE}
                    </div>
                  )}
                </div>
              </button>
              <div className="p-3">
                <div className="flex items-start gap-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-secondary">
                      {title}
                    </p>
                    {library?.fileName && library.fileName !== title ? (
                      <p className="truncate text-xs text-tertiary">
                        {library.fileName}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      setRenameItem(item);
                    }}
                    className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-secondary transition hover:bg-gray"
                    aria-label={`Rename ${title}`}
                    title="Rename"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                </div>
                <p className="mt-1 text-xs text-tertiary">
                  {kind}
                  {hasSheet ? " · Design sheet" : ""}
                  {savedLabel ? ` · ${savedLabel}` : ""}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <ImageGeneratorLibraryModal
        open={Boolean(renameItem)}
        submitting={renaming}
        rename={Boolean(
          renameItem?.review?.library?.fileName ||
            renameItem?.review?.library?.name
        )}
        defaultFileName={
          renameItem?.review?.library?.fileName ||
          renameItem?.review?.library?.name ||
          (renameItem ? imageGeneratorSessionTitle(renameItem) : "")
        }
        defaultName={
          renameItem?.review?.library?.name ||
          renameItem?.review?.library?.fileName ||
          (renameItem ? imageGeneratorSessionTitle(renameItem) : "")
        }
        onClose={() => {
          if (!renaming) {
            setRenameItem(null);
          }
        }}
        onSubmit={handleRename}
      />
    </div>
  );
};

export default ImageGeneratorTracker;
